import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { Zap, Plus, Trash2, Pencil, Search, Loader2, X, Check, Cpu, Paperclip, Upload, FileText, AlertTriangle, Warehouse } from "lucide-react";

const inputCls = "w-full h-11 rounded-lg bg-slate-900 border border-slate-700 px-4 text-sm outline-none focus:border-amber-500 transition-colors";
const empty = { name: "", brand: "", codice_prodotto: "", codice_azienda: "", line_ids: [], warehouse_id: "", notes: "" };

function AttachmentsDialog({ item, onClose, onChanged }) {
  const [uploading, setUploading] = useState(false);
  const atts = item.attachments || [];

  const upload = async (files) => {
    setUploading(true);
    try {
      for (const f of Array.from(files)) {
        const fd = new FormData();
        fd.append("file", f);
        await api.post(`/electrical/${item.id}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success("Scheda tecnica caricata.");
      onChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setUploading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 w-full max-w-lg rounded-xl border border-slate-700 bg-slate-900 p-6 shadow-2xl max-h-[90vh] overflow-auto" data-testid="attachments-dialog">
        <button onClick={onClose} className="absolute top-4 right-4 text-slate-500 hover:text-slate-200"><X className="h-5 w-5" /></button>
        <h3 className="font-heading text-xl font-bold">{item.name}</h3>
        <p className="text-sm text-slate-500 font-mono-data">{item.brand} · {item.codice_prodotto}</p>

        <label className="mt-4 flex items-center gap-3 h-11 px-4 rounded-lg border border-dashed border-slate-700 hover:border-amber-500 cursor-pointer text-sm text-slate-400 transition-colors" data-testid="el-attach-upload">
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
          Carica datasheet / foto (PDF, JPG, PNG)…
          <input type="file" multiple className="hidden" onChange={(e) => upload(e.target.files)} />
        </label>

        <div className="mt-4 space-y-2">
          <div className="text-slate-500 text-xs uppercase tracking-wider">Schede tecniche ({atts.length})</div>
          {atts.length === 0 && <p className="text-slate-600 text-sm">Nessun allegato.</p>}
          {atts.map((a, i) => {
            const url = `${API}/files/${a.storage_path}`;
            const isImg = (a.content_type || "").startsWith("image/");
            return (
              <a key={i} href={url} target="_blank" rel="noreferrer" className="flex items-center gap-2 rounded-lg border border-slate-700 hover:border-amber-500 px-3 py-2.5 text-sm transition-colors">
                {isImg ? <img src={url} alt="" className="h-8 w-8 object-cover rounded" /> : <FileText className="h-5 w-5 text-amber-400" />}
                <span className="truncate">{a.original_filename}</span>
              </a>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default function Elettrici() {
  const [items, setItems] = useState([]);
  const [lines, setLines] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const [lineFilter, setLineFilter] = useState("");
  const [brandFilter, setBrandFilter] = useState("");
  const [attItem, setAttItem] = useState(null);

  const load = async () => {
    const [e, l] = await Promise.all([api.get("/electrical"), api.get("/lines")]);
    setItems(e.data);
    setLines(l.data);
    if (attItem) { const fresh = e.data.find((x) => x.id === attItem.id); if (fresh) setAttItem(fresh); }
    try { const w = await api.get("/warehouse"); setWarehouses(w.data); } catch (_) { setWarehouses([]); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleLine = (id) => setForm((f) => ({ ...f, line_ids: f.line_ids.includes(id) ? f.line_ids.filter((x) => x !== id) : [...f.line_ids, id] }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.brand || !form.codice_prodotto || !form.codice_azienda)
      return toast.error("Compila nome, marca, codice prodotto e codice azienda.");
    setBusy(true);
    try {
      if (editId) { await api.put(`/electrical/${editId}`, form); toast.success("Componente aggiornato."); }
      else { await api.post("/electrical", form); toast.success("Componente aggiunto."); }
      setForm(empty); setEditId(null); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  const startEdit = (it) => {
    setEditId(it.id);
    setForm({ name: it.name || "", brand: it.brand || "", codice_prodotto: it.codice_prodotto || "", codice_azienda: it.codice_azienda || "", line_ids: it.line_ids || [], warehouse_id: it.warehouse_id || "", notes: it.notes || "" });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const del = async (id) => {
    if (!window.confirm("Eliminare questo componente elettrico?")) return;
    await api.delete(`/electrical/${id}`);
    toast.success("Eliminato.");
    if (editId === id) { setEditId(null); setForm(empty); }
    load();
  };

  const brands = useMemo(() => [...new Set(items.map((i) => i.brand).filter(Boolean))].sort((a, b) => a.localeCompare(b)), [items]);

  const filtered = useMemo(() => items.filter((i) => {
    if (lineFilter && !(i.line_ids || []).includes(lineFilter)) return false;
    if (brandFilter && i.brand !== brandFilter) return false;
    if (q) { const t = `${i.name} ${i.brand} ${i.codice_prodotto} ${i.codice_azienda}`.toLowerCase(); if (!t.includes(q.toLowerCase())) return false; }
    return true;
  }), [items, q, lineFilter, brandFilter]);

  return (
    <div className="space-y-6 animate-fade-up">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Componenti Elettrici</h1>
        <p className="text-slate-500 text-sm mt-1">Componenti elettrici per macchinario, con marca, codici, schede tecniche e giacenza di magazzino.</p>
      </div>

      <form onSubmit={submit} className="rounded-lg border border-slate-800 bg-slate-900/60 p-5 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-lg font-bold flex items-center gap-2"><Zap className="h-5 w-5 text-amber-400" /> {editId ? "Modifica componente" : "Nuovo componente elettrico"}</h2>
          {editId && <button type="button" onClick={() => { setEditId(null); setForm(empty); }} className="text-xs text-slate-400 hover:text-slate-200 flex items-center gap-1"><X className="h-3.5 w-3.5" /> Annulla modifica</button>}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <input data-testid="el-name" className={inputCls} placeholder="Nome componente" value={form.name} onChange={(e) => set("name", e.target.value)} />
          <input data-testid="el-brand" className={inputCls} placeholder="Marca" value={form.brand} onChange={(e) => set("brand", e.target.value)} />
          <input data-testid="el-codice-prodotto" className={inputCls} placeholder="Codice prodotto" value={form.codice_prodotto} onChange={(e) => set("codice_prodotto", e.target.value)} />
          <input data-testid="el-codice-azienda" className={inputCls} placeholder="Codice azienda" value={form.codice_azienda} onChange={(e) => set("codice_azienda", e.target.value)} />
          <select data-testid="el-warehouse" className={`${inputCls} sm:col-span-2`} value={form.warehouse_id} onChange={(e) => set("warehouse_id", e.target.value)}>
            <option value="">Nessun collegamento a magazzino</option>
            {warehouses.map((w) => <option key={w.id} value={w.id}>{`${w.name} (${w.codice_prodotto}) · giacenza ${w.quantity ?? 0}`}</option>)}
          </select>
          <input data-testid="el-notes" className={`${inputCls} sm:col-span-2`} placeholder="Note (opzionale)" value={form.notes} onChange={(e) => set("notes", e.target.value)} />
        </div>
        <div className="mt-4">
          <div className="text-xs font-mono-data uppercase tracking-widest text-slate-400 mb-2">Compatibile con i macchinari ({form.line_ids.length})</div>
          <div className="flex flex-wrap gap-2">
            {lines.map((l) => {
              const sel = form.line_ids.includes(l.id);
              return (
                <button type="button" key={l.id} data-testid={`el-line-${l.id}`} onClick={() => toggleLine(l.id)} className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs transition-colors ${sel ? "border-amber-500/50 bg-amber-500/10 text-amber-400" : "border-slate-700 bg-slate-950/40 text-slate-400 hover:border-slate-500"}`}>
                  {sel && <Check className="h-3 w-3" />}<Cpu className="h-3 w-3" /> {l.name}
                </button>
              );
            })}
            {lines.length === 0 && <span className="text-slate-500 text-sm">Nessun macchinario/linea in anagrafica.</span>}
          </div>
        </div>
        <div className="mt-4">
          <button type="submit" disabled={busy} data-testid="el-save" className="h-11 px-5 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold text-sm flex items-center gap-2 disabled:opacity-60">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} {editId ? "Salva modifiche" : "Aggiungi componente"}
          </button>
        </div>
      </form>

      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
          <input data-testid="el-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per nome, marca o codice…" className="w-full h-11 rounded-lg bg-slate-900 border border-slate-700 pl-10 pr-4 text-sm outline-none focus:border-amber-500" />
        </div>
        <select data-testid="el-filter-brand" value={brandFilter} onChange={(e) => setBrandFilter(e.target.value)} className="h-11 rounded-lg bg-slate-900 border border-slate-700 px-4 text-sm outline-none focus:border-amber-500">
          <option value="">Tutte le marche</option>
          {brands.map((b) => <option key={b} value={b}>{b}</option>)}
        </select>
        <select data-testid="el-filter-line" value={lineFilter} onChange={(e) => setLineFilter(e.target.value)} className="h-11 rounded-lg bg-slate-900 border border-slate-700 px-4 text-sm outline-none focus:border-amber-500">
          <option value="">Tutti i macchinari</option>
          {lines.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select>
      </div>

      {brands.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="el-brand-chips">
          <button onClick={() => setBrandFilter("")} data-testid="el-brand-chip-all" className={`rounded-full border px-3 py-1 text-xs transition-colors ${brandFilter === "" ? "border-amber-500/50 bg-amber-500/10 text-amber-400" : "border-slate-700 bg-slate-950/40 text-slate-400 hover:border-slate-500"}`}>Tutte</button>
          {brands.map((b) => (
            <button key={b} onClick={() => setBrandFilter(b)} data-testid={`el-brand-chip-${b}`} className={`rounded-full border px-3 py-1 text-xs transition-colors ${brandFilter === b ? "border-amber-500/50 bg-amber-500/10 text-amber-400" : "border-slate-700 bg-slate-950/40 text-slate-400 hover:border-slate-500"}`}>{b}</button>
          ))}
        </div>
      )}

      <div className="rounded-lg border border-slate-800 bg-slate-900/60 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="electrical-table">
            <thead>
              <tr className="border-b border-slate-800 text-left text-xs font-mono-data uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3">Componente</th>
                <th className="px-4 py-3">Marca</th>
                <th className="px-4 py-3">Cod. Prodotto</th>
                <th className="px-4 py-3">Macchinari</th>
                <th className="px-4 py-3">Giacenza</th>
                <th className="px-4 py-3 text-right">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((i) => (
                <tr key={i.id} data-testid={`el-row-${i.id}`} className="border-b border-slate-800/60 hover:bg-slate-800/40 transition-colors">
                  <td className="px-4 py-3 font-medium">{i.name}{i.attachments?.length > 0 && <Paperclip className="inline h-3.5 w-3.5 ml-1.5 text-slate-500" />}</td>
                  <td className="px-4 py-3 text-slate-300">{i.brand}</td>
                  <td className="px-4 py-3 font-mono-data text-amber-400">{i.codice_prodotto}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {(i.line_names || []).length === 0 && <span className="text-slate-600 text-xs">—</span>}
                      {(i.line_names || []).map((n, idx) => <span key={idx} className="text-[10px] px-2 py-0.5 rounded-full bg-slate-800 text-slate-300 border border-slate-700">{n}</span>)}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {i.stock ? (
                      <span className="inline-flex items-center gap-1.5 font-mono-data text-slate-300">
                        {i.stock.quantity}{i.stock.reorder_threshold ? <span className="text-slate-600">/ {i.stock.reorder_threshold}</span> : null}
                        {i.stock.low_stock && <span data-testid={`el-low-${i.id}`} className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-rose-500/10 text-rose-400 border border-rose-500/30"><AlertTriangle className="h-3 w-3" /> riordino</span>}
                      </span>
                    ) : <span className="text-slate-600 text-xs inline-flex items-center gap-1"><Warehouse className="h-3.5 w-3.5" />—</span>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => setAttItem(i)} data-testid={`el-attachments-${i.id}`} className="p-2 rounded-lg text-slate-400 hover:text-amber-400 hover:bg-slate-800" title="Schede tecniche"><Paperclip className="h-4 w-4" /></button>
                      <button onClick={() => startEdit(i)} data-testid={`el-edit-${i.id}`} className="p-2 rounded-lg text-slate-400 hover:text-amber-400 hover:bg-slate-800"><Pencil className="h-4 w-4" /></button>
                      <button onClick={() => del(i.id)} data-testid={`el-delete-${i.id}`} className="p-2 rounded-lg text-slate-400 hover:text-rose-400 hover:bg-slate-800"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-500">Nessun componente elettrico.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {attItem && <AttachmentsDialog item={attItem} onClose={() => setAttItem(null)} onChanged={load} />}
    </div>
  );
}
